import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { LegendBoxGreen, LegendBoxRed, LegendWrapper, OffersWrapper } from 'components/AvailabilityDatePicker/AvailabilityHelpers';
import { OffersList } from 'components/AvailabilityDatePicker/OffersList';
import { AvailabilityComponent, useAvailabilityDatePicker } from 'components/AvailabilityDatePicker/useAvailability';
import { MinimalHotelCard } from 'components/MinimalHotelCard';
import { DatesTypes, SoldOutBodyMessage } from 'components/SearchUnavailability/SoldOut/SoldOutBodyMessage';
import { TaxationAndNightlyPriceMessage } from 'components/TaxationAndNightlyPriceMessage';
import { SoldOutBase } from 'components/Unavailability/SoldOut/SoldOutBase';
import { UnavailabilityWrapper } from 'components/Unavailability/UnavailabilityComponent/UnavailabilityWithPopupTrigger';
import hotelPlaceholderImage from 'images/placeholder-hotel-image.svg';
import { getAnchorHotel, getAnchorHotelId, getHotelAvailabilityPrices, getHotelAvailabilityPricesComplete, getSearchParameters, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getSelectedAvailabilityDate } from 'modules/search/selectors';
import { getSearchBoxValues } from 'modules/searchBox/selectors';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Text } from '@daedalus/atlas/Text';
import { ConfigureI18nContext } from '@daedalus/core/src/localization/components/ConfigureI18n';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { AvailabilityDatePicker } from '../../../../shared/src/search/DatePicker/AvailabilityDatePicker';
const Container = /*#__PURE__*/styled('div')({
  name: "Container",
  class: "c1gcv2yz",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => Flex.Column;
const LeftSide = /*#__PURE__*/styled(_exp3())({
  name: "LeftSide",
  class: "l1oax0sb",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => Flex.Row;
const Legend = /*#__PURE__*/styled(_exp6())({
  name: "Legend",
  class: "l1g3tz8p",
  propsAsIs: true
});
const _exp9 = /*#__PURE__*/() => Flex.Column;
const RightSide = /*#__PURE__*/styled(_exp9())({
  name: "RightSide",
  class: "rqcddxi",
  propsAsIs: true
});
const HotelCardWrapper = /*#__PURE__*/styled('div')({
  name: "HotelCardWrapper",
  class: "hmwenq4",
  propsAsIs: false
});
const CapitalizedFormattedMessage = /*#__PURE__*/styled('span')({
  name: "CapitalizedFormattedMessage",
  class: "c159nqbl",
  propsAsIs: false
});
export const AvailabilityPopupContent = () => {
  const [initialSearchComplete, setInitialSearchComplete] = useState<boolean>(false);
  const hotel = useSelector(getAnchorHotel);
  const anchorHotelId = useSelector(getAnchorHotelId);
  const {
    rooms
  } = useSelector(getSearchBoxValues) || {};
  const {
    imageURIs,
    hotelName
  } = hotel || {};
  const imageUrl = imageURIs[0] || '';
  const {
    monthNames,
    weekDayNamesShort,
    firstDayOfWeek
  } = useContext(ConfigureI18nContext);
  const selectedAvailabilityDate = useSelector(getSelectedAvailabilityDate);
  const hotelAvailabilityPrices = useSelector(state => getHotelAvailabilityPrices(state, anchorHotelId));
  const {
    checkIn
  } = useSelector(getSearchParameters);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const searchComplete = useSelector(getHotelAvailabilityPricesComplete);
  const selectedDateOffers = hotelAvailabilityPrices && hotelAvailabilityPrices[selectedAvailabilityDate]?.offers;
  const component = AvailabilityComponent.DesktopAvailabilityCalendar;
  const {
    handleDayClick,
    handleMonthChange
  } = useAvailabilityDatePicker({
    component
  });
  useEffect(() => {
    if (searchComplete) {
      setInitialSearchComplete(true);
    }
  }, [searchComplete]);
  return <Container>
      <LeftSide gap="s500">
        <AvailabilityDatePicker numberOfMonthsToShow={1} firstDayOfWeek={firstDayOfWeek} weekdaysShort={weekDayNamesShort} showWeekDays months={monthNames} selectedDate={selectedAvailabilityDate} hotelAvailabilityPrices={hotelAvailabilityPrices} checkIn={checkIn} onMonthChange={handleMonthChange} onDayClick={handleDayClick} showTotalPrices={showTotalPrices} searchComplete={searchComplete} />
        <Legend>
          <Text variant="bodyXS" colorPath="content.neutral.c600">
            {showTotalPrices ? <>
                <CapitalizedFormattedMessage>
                  <FormattedMessageWrapper id="payment.total" defaultMessage="total" />
                </CapitalizedFormattedMessage>
                &nbsp;
                <FormattedMessageWrapper id="includingTaxesShort" defaultMessage="incl tax & fees" />
              </> : <TaxationAndNightlyPriceMessage showCurrencyCode hotelId={anchorHotelId} />}
          </Text>
          <LegendWrapper>
            <LegendBoxGreen />
            <Text variant="bodyXS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper id="cheaper" defaultMessage="Cheaper" />
            </Text>
            <LegendBoxRed />
            <Text variant="bodyXS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper id="higher" defaultMessage="Higher" />
            </Text>
          </LegendWrapper>
        </Legend>
      </LeftSide>
      <RightSide gap="s500">
        <HotelCardWrapper>
          <MinimalHotelCard title={hotelName} imageUrl={imageUrl} fullWidth rooms={rooms} placeholderImage={hotelPlaceholderImage} isCompactImage />
        </HotelCardWrapper>

        {!selectedDateOffers && selectedAvailabilityDate && searchComplete && <UnavailabilityWrapper backgroundColorPath="background.neutral.c050" borderRadius="lg">
            <SoldOutBase headerSlot={<SoldOutBodyMessage datesType={DatesTypes.Availability} />} contentSlot={<FormattedMessageWrapper id="noAvailabilityCheckCalendar" defaultMessage="Your selected dates are not available. Check out other dates on the calendar." />} />
          </UnavailabilityWrapper>}
        {selectedDateOffers && initialSearchComplete && <OffersWrapper className="__fullWidth">
            <OffersList hotelId={anchorHotelId} offers={selectedDateOffers} sourceComponent={component} />
          </OffersWrapper>}
      </RightSide>
    </Container>;
};

require("./AvailabilityPopupContent.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AvailabilityPopupContent.tsx");